import { motion } from "framer-motion";
import { Redirect, useLocation } from "wouter";
import type { QuizQuestion } from "@shared/types";
import { useProfile } from "@/lib/api";
import { ProfileIdentityHeader } from "@/components/ProfileIdentityHeader";
import { ProfileBio } from "@/components/profile/ProfileBio";
import { ProfileAboutSection } from "@/components/ProfileAboutSection";
import { ProfileBasicInfoSection } from "@/components/ProfileBasicInfoSection";
import { PageHeader } from "@/components/ux/PageHeader";
import { useAllQuizQuestions } from "@/data/quiz";
import { Loader } from "@/components/Loader";

const asArray = (v: unknown): string[] => (Array.isArray(v) ? (v as string[]) : []);

const humanize = (value: string) =>
  value.replace(/[_-]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/** Labels for the user's pronoun answers, falling back to the humanized value. */
function pronounLabels(questions: QuizQuestion[], raw: unknown): string[] {
  const options = questions.find((q) => q.id === "pronouns")?.options;
  return asArray(raw).map(
    (v) => options?.find((o) => o.value === v)?.label ?? humanize(String(v)),
  );
}

// The public profile editor: photo, name, bio, "About you" and basic info.
// Reached from the "Edit profile" button on the account page.
export default function EditProfile() {
  const [, navigate] = useLocation();
  const { data: profileData, isLoading } = useProfile({ staleTime: 0 });
  const allQuestions = useAllQuizQuestions();

  const profile = profileData?.data?.profile as any;
  const user = profileData?.data?.user as any;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <Loader />
      </div>
    );
  }

  // Nothing to edit before the quiz is done; the account page prompts for it.
  if (!profile) return <Redirect to="/profile" />;

  return (
    <div className="pt-4 pb-24 md:pt-8 px-4">
      <div className="max-w-2xl mx-auto">
        <PageHeader title="Edit profile" onBack={() => navigate("/profile")} />

        <div className="space-y-6">
          {/* Head — identity + bio, open on the page (no card) to match the
              headered sections below. */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <ProfileIdentityHeader
              userId={user?.id}
              firstName={user?.firstName}
              lastName={user?.lastName}
              profileImageUrl={user?.profileImageUrl}
              birthday={profile.birthday}
              cityId={user?.city}
              cityName={user?.cityName}
              pronounLabels={pronounLabels(allQuestions, profile.pronouns)}
            />

            <div className="mt-3">
              <ProfileBio bio={profile.bio ?? null} />
            </div>
          </motion.div>

          {/* Public, expressive layer — "About you" prompts (shown to others) */}
          <ProfileAboutSection
            interestTags={asArray(profile.interestTags)}
            currentlyConsuming={asArray(profile.currentlyConsuming)}
            lookingTo={asArray(profile.lookingTo)}
          />

          {/* Basic info — education, work, identity */}
          <ProfileBasicInfoSection
            education={profile.education ?? null}
            work={profile.work ?? null}
            pronouns={asArray(profile.pronouns)}
            genderIdentity={asArray(profile.genderIdentity)}
            sexualOrientation={asArray(profile.sexualOrientation)}
            relationshipStatus={profile.relationshipStatus ?? null}
          />
        </div>
      </div>
    </div>
  );
}
